import * as Sentry from "@sentry/react";

import {
  call,
  put,
  retry,
  select,
  takeEvery,
  takeLatest,
} from "redux-saga/effects";

import { AxiosResponse } from "axios";
import { BASE_API_URL } from "../../config";
import { ReduxState } from "../rootReducer";
import { actions } from "./slice";
import { createRequest } from "../../utils/requestUtils";
import { replaceUrlSlashAndDot } from "../interview/utils";
import { sanitizeString } from "../../utils/sentryUtils";

function* createPrimaryPhysician({
  payload,
}: ReturnType<typeof actions.requestCreatePrimaryPhysician>) {
  const { caseId, physicianCommand } = payload;
  const url = `${BASE_API_URL}/cases/${caseId}/primaryPhysician`;
  try {
    const { data }: AxiosResponse = yield call(createRequest, {
      method: "post",
      url: url,
      requestBody: physicianCommand,
    });

    yield put(actions.requestCreatePrimaryPhysicianSuccessful(data));
  } catch (error) {
    /*
     * Sentry logging to easily identify problematic questions etc.
     * This should be made more generic
     */
    Sentry.setTag("apiUrl", sanitizeString(url));
    Sentry.setTag("function", "createPrimaryPhysician");
    Sentry.setContext("saga", {
      name: "Physician",
      function: "createPrimaryPhysician",
      href: sanitizeString(url),
    });
    Sentry.captureEvent({
      message: "Unable to create primary physician",
    });
    const errorMessage = error instanceof Error ? error.toString() : "error";
    yield put(actions.requestCreatePrimaryPhysicianFailed(errorMessage));
  }
}

function* updatePrimaryPhysician({
  payload,
}: ReturnType<typeof actions.requestCreatePrimaryPhysician>) {
  const { caseId, physicianCommand } = payload;
  const url = `${BASE_API_URL}/cases/${caseId}/primaryPhysician`;
  try {
    const { data }: AxiosResponse = yield call(createRequest, {
      method: "patch",
      url: url,
      requestBody: physicianCommand,
    });
    yield put(actions.requestUpdatePrimaryPhysicianSuccessful(data));
  } catch (error) {
    /*
     * Sentry logging to easily identify problematic questions etc.
     * This should be made more generic
     */
    Sentry.setTag("apiUrl", sanitizeString(url));
    Sentry.setTag("function", "updatePrimaryPhysician");
    Sentry.setContext("saga", {
      name: "Physician",
      function: "updatePrimaryPhysician",
      href: sanitizeString(url),
    });
    Sentry.captureEvent({
      message: "Unable to update primary physician",
    });
    const errorMessage = error instanceof Error ? error.toString() : "error";
    yield put(actions.requestUpdatePrimaryPhysicianFailed(errorMessage));
  }
}

function* deletePrimaryPhysician({
  payload,
}: ReturnType<typeof actions.requestDeletePrimaryPhysician>) {
  const { caseId } = payload;
  const url = `${BASE_API_URL}/cases/${caseId}/primaryPhysician`;
  try {
    yield call(createRequest, {
      method: "delete",
      url: url,
    });
  } catch (error) {
    /*
     * Sentry logging to easily identify problematic questions etc.
     * This should be made more generic
     */
    Sentry.setTag("apiUrl", sanitizeString(url));
    Sentry.setTag("function", "deletePrimaryPhysician");
    Sentry.setContext("saga", {
      name: "Physician",
      function: "deletePrimaryPhysician",
      href: sanitizeString(url),
    });
    Sentry.captureEvent({
      message: "Unable to delete primary physician",
    });
    const errorMessage = error instanceof Error ? error.toString() : "error";
    yield put(actions.requestDeletePrimaryPhysicianFailed(errorMessage));
  }
}

function* createDisclosurePhysician({
  payload,
}: ReturnType<typeof actions.requestCreateDisclosurePhysician>) {
  const { caseId } = yield select(
    (state: ReduxState) => state.interview.questions,
  );

  const {
    disclosureName,
    disclosureQuestions,
    baseQuestionCode,
    physicianResource,
  } = payload;

  const deleteUrl = `${BASE_API_URL}/cases/${caseId}/questions/${baseQuestionCode}/disclosures/${replaceUrlSlashAndDot(
    disclosureName,
  )}`;
  const recreateUrl = `${BASE_API_URL}/cases/${caseId}/baseQuestions/${baseQuestionCode}/disclosures?expand=true`;
  const addUrl = `${BASE_API_URL}/cases/${caseId}/disclosures/${disclosureName}/physicians`;

  try {
    /* Below code is a workaround required to ensure disclosures are marked as completed when
     * disclosurePhysician is set.
     */

    // Delete disclosure.
    yield call(createRequest, {
      method: "delete",
      url: deleteUrl,
    });

    // Recreate disclosure with physician flags set.
    const { data: updatedDisclosure } = yield call(createRequest, {
      method: "post",
      url: recreateUrl,
      requestBody: {
        aliasName: disclosureName,
        hasPhysicians: true,
        physicianCaptureComplete: true,
        questions: disclosureQuestions,
      },
    });

    // Create add physician to disclosure
    const { data: disclosurePhysician }: AxiosResponse = yield call(
      createRequest,
      {
        method: "post",
        url: addUrl,
        requestBody: physicianResource,
      },
    );

    yield put(
      actions.requestCreateDisclosurePhysicianSuccessful({
        disclosurePhysician: disclosurePhysician,
        updatedDisclosure: updatedDisclosure,
        physicianResource: physicianResource,
      }),
    );
  } catch (error) {
    /*
     * Sentry logging to easily identify problematic questions etc.
     * This should be made more generic
     */
    Sentry.setTag("apiUrl", sanitizeString(deleteUrl));
    Sentry.setTag("apiUrl", sanitizeString(recreateUrl));
    Sentry.setTag("apiUrl", sanitizeString(addUrl));
    Sentry.setTag("function", "createDisclosurePhysician");
    Sentry.setContext("saga", {
      name: "Physician",
      function: "createDisclosurePhysician",
      deleteUrl: sanitizeString(deleteUrl),
      recreateUrl: sanitizeString(recreateUrl),
      addUrl: sanitizeString(addUrl),
    });
    Sentry.captureEvent({
      message: "Unable to create disclosure physician",
    });

    yield put(actions.requestCreateDisclosurePhysicianFailed());
  }
}

function* updateDisclosurePhysician({
  payload,
}: ReturnType<typeof actions.requestUpdateDisclosurePhysician>) {
  const { caseId } = yield select(
    (state: ReduxState) => state.interview.questions,
  );

  const { disclosureName, physicianResource, physicianUuid } = payload;
  const url = `${BASE_API_URL}/cases/${caseId}/disclosures/${disclosureName}/physicians/${physicianUuid}`;
  try {
    const { data }: AxiosResponse = yield call(createRequest, {
      method: "patch",
      url: url,
      requestBody: physicianResource,
    });

    yield put(actions.requestUpdateDisclosurePhysicianSuccessful(data));
  } catch (error) {
    /*
     * Sentry logging to easily identify problematic questions etc.
     * This should be made more generic
     */
    Sentry.setTag("apiUrl", sanitizeString(url));
    Sentry.setTag("function", "updateDisclosurePhysician");
    Sentry.setContext("saga", {
      name: "Physician",
      function: "createDisclosurePhysician",
      href: sanitizeString(url),
    });
    Sentry.captureEvent({
      message: "Unable to update disclosure physician",
    });

    yield put(actions.requestUpdateDisclosurePhysicianFailed());
  }
}

export function* fetchPhysicianInformation({
  payload,
}: ReturnType<typeof actions.requestPhysicianInformation>) {
  const physicianSearch = payload;
  const url = `${BASE_API_URL}/doctors?searchTerm=${encodeURIComponent(
    physicianSearch,
  )}`;

  try {
    const { data }: AxiosResponse = yield retry(3, 1000, createRequest, {
      url: url,
    });
    yield put(actions.requestPhysicianInformationSuccessful(data));
  } catch (error) {
    /*
     * Sentry logging to easily identify problematic questions etc.
     * This should be made more generic
     */
    Sentry.setTag("apiUrl", sanitizeString(url));
    Sentry.setTag("function", "fetchPhysicianInformation");
    Sentry.setContext("saga", {
      name: "Physician",
      function: "fetchPhysicianInformation",
      href: sanitizeString(url),
    });
    Sentry.captureEvent({
      message: "Unable to physician information",
    });
    const errorMessage = error instanceof Error ? error.toString() : "error";
    yield put(actions.requestPhysicianInformationFailed(errorMessage));
  }
}

function* physicianSaga() {
  yield takeEvery(
    actions.requestCreatePrimaryPhysician,
    createPrimaryPhysician,
  );
  yield takeEvery(
    actions.requestUpdatePrimaryPhysician,
    updatePrimaryPhysician,
  );
  yield takeEvery(
    actions.requestDeletePrimaryPhysician,
    deletePrimaryPhysician,
  );
  yield takeLatest(
    actions.requestCreateDisclosurePhysician,
    createDisclosurePhysician,
  );
  yield takeLatest(
    actions.requestUpdateDisclosurePhysician,
    updateDisclosurePhysician,
  );
  yield takeLatest(
    actions.requestPhysicianInformation,
    fetchPhysicianInformation,
  );
}

export default physicianSaga;
